import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { parseErrorMessage } from "@/lib/queryClient";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { VoiceAudioCacheReason, VoiceAudioCacheStats } from "./types";
import { VOICE_CACHE_REASON_LABELS } from "./constants";
import { formatBytes } from "./helpers";

export function VoiceAudioCachePanel() {
  const cacheQuery = useQuery<VoiceAudioCacheStats>({
    queryKey: ["/api/admin/pulse/voice-audio-cache"],
    queryFn: async () => {
      const res = await fetch("/api/admin/pulse/voice-audio-cache", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load voice audio cache stats");
      return res.json();
    },
    refetchInterval: 30_000,
  });

  const data = cacheQuery.data;
  const entryPct = data ? Math.min(100, Math.round((data.entries / data.maxEntries) * 100)) : 0;
  const bytePct = data ? Math.min(100, Math.round((data.bytes / data.maxBytes) * 100)) : 0;
  const ttlMin = data ? Math.round(data.ttlMs / 60_000) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-voice-audio-cache-title">
            Pulse Voice Briefing Audio Cache
          </h3>
          <p className="text-sm text-muted-foreground">
            In-process cache (per replica) of synthesized briefing MP3s. Bounded by TTL ({ttlMin} min), entry count, and total bytes. Watch eviction reasons to tune the caps.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => cacheQuery.refetch()}
          disabled={cacheQuery.isFetching}
          data-testid="button-refresh-voice-audio-cache"
        >
          {cacheQuery.isFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {cacheQuery.isLoading ? (
        <Card className="p-6"><Skeleton className="h-20 w-full" /></Card>
      ) : cacheQuery.error ? (
        <Card className="p-6">
          <p className="text-sm text-red-600" data-testid="text-voice-audio-cache-error">
            {parseErrorMessage(cacheQuery.error, "Failed to load voice audio cache stats")}
          </p>
        </Card>
      ) : data ? (
        <>
          <Card className="p-6" data-testid="card-voice-audio-cache-summary">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Entries</div>
                <div className="text-2xl font-semibold tabular-nums" data-testid="text-voice-audio-cache-entries">
                  {data.entries.toLocaleString()}{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    / {data.maxEntries.toLocaleString()} ({entryPct}%)
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total bytes</div>
                <div className="text-2xl font-semibold tabular-nums" data-testid="text-voice-audio-cache-bytes">
                  {formatBytes(data.bytes)}{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    / {formatBytes(data.maxBytes)} ({bytePct}%)
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6" data-testid="card-voice-audio-cache-evictions">
            <div className="flex items-baseline justify-between mb-3">
              <h4 className="text-sm font-semibold">Evictions in the last hour</h4>
              <span className="text-sm tabular-nums" data-testid="text-voice-audio-cache-evictions-total">
                {data.evictionsLastHour.total.toLocaleString()} total
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(Object.keys(VOICE_CACHE_REASON_LABELS) as VoiceAudioCacheReason[]).map((reason) => (
                  <TableRow key={reason} data-testid={`row-voice-audio-cache-reason-${reason}`}>
                    <TableCell className="text-sm">{VOICE_CACHE_REASON_LABELS[reason]}</TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      data-testid={`text-voice-audio-cache-reason-count-${reason}`}
                    >
                      {data.evictionsLastHour[reason].toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      ) : null}
    </div>
  );
}
